// =====================================================================================
// Cloud subscription status for the till — PURE rules (no storage, no network, no clock of their
// own), so they can be checked on their own.
//
// This file exists twice and the two copies are kept BYTE-IDENTICAL (check with `cmp`), so the web
// notice bar and the desktop till say the same thing at the same time — change both or neither:
//   desktop  src/utils/subscriptionStatus.ts               (I/O: services/subscriptionService.ts)
//   web      src/Common/subscription/subscriptionStatus.ts  (I/O: components/SubscriptionNoticeBar.tsx)
//
// Source: GET CompanySubscriptionsPlan/current-plan/{companyId} — the call the web sidebar's
// "Upgrade to PRO (6 days left)" button uses. Billing stops when the subscription or trial ends:
// the server said IsExpired, or the (trusted) clock has passed the end. Never earlier than the real
// end: without the exact end (older server) the latest moment the date / day count allow is used.
//
// Pay as you go (billing type "PerBill") is OPEN-ENDED: the subscription runs to 31 Dec 2099 (the
// server's BillingDates.OpenEndedUntilUtc) and is charged per bill. It never ends here, gets no
// expiry notice and shows "Pay as you go" instead of days left / an end date. Only the server stops
// it (suspending or cancelling ends the row, so the server answers IsExpired). Older servers send
// only the date, so an end in 2099 or later counts as open-ended too (BillingDates.IsOpenEnded).
// =====================================================================================

/** The top-of-screen notice shows from this many days before the end (paid plans; trials always). */
export const SUBSCRIPTION_WARN_DAYS = 15;

const DAY_MS = 24 * 60 * 60 * 1000;

/** An end at or after this moment means "no end" (the server's BillingDates.IsOpenEnded: ≥ 1 Jan 2099 UTC). */
export const OPEN_ENDED_FROM_MS = Date.UTC(2099, 0, 1);

/** Shown instead of days left / an end date for an open-ended (pay-as-you-go) plan. */
export const PAY_AS_YOU_GO_LABEL = 'Pay as you go';

export const SUBSCRIPTION_EXPIRED_MESSAGE =
  'Your subscription has expired. Please renew your plan to continue processing bills.';
export const TRIAL_EXPIRED_MESSAGE =
  'Your free trial has ended. Please upgrade your plan to continue processing bills.';

/** How the plan is charged: a fixed period, or per sale bill ('PerBill' = pay as you go). */
export type SubscriptionBillingType = 'Subscription' | 'PerBill';

/** One answer of the current-plan call, kept so an offline till still knows. */
export interface SubscriptionSnapshot {
  companyId: string;
  packageName: string;
  isTrial: boolean;
  /** The server's verdict when it answered: no subscription runs past "now". */
  isExpired: boolean;
  /** Whole days left when the server answered (its own (EndDate − now).Days). */
  daysRemaining: number;
  /** "yyyy-MM-dd" — the UTC date of the end. */
  endDate: string | null;
  /** The exact end in ms (servers that send EndDateUtc); null before that. */
  endUtcMs: number | null;
  /** When the server answered, on the server's clock when it sent one (ms). */
  fetchedAtMs: number;
  /** Server clock − this PC's clock when it answered (0 when the server sent no time). */
  serverOffsetMs: number;
  /** True when fetchedAtMs / serverOffsetMs come from the server's own clock. */
  serverClock: boolean;
  // Pay as you go. Optional: answers stored before it existed have none of these (a fixed-period
  // plan — though an end in 2099 still counts as open-ended, see isOpenEndedSnapshot).
  /** 'PerBill' = pay as you go (charged per sale bill). */
  billingType?: SubscriptionBillingType;
  /** No end: the server's isOpenEnded, or an end in 2099 or later (older servers). */
  isOpenEnded?: boolean;
  /** Pay as you go: the charge per bill, when the server sent it. */
  pricePerBill?: number | null;
  /** ISO code of the plan's currency ("PKR"), when the server sent it. */
  currencyCode?: string | null;
  /** The currency's display symbol as the server sent it ("Rs"). */
  currencySymbol?: string | null;
}

export type SubscriptionNoticeTone = 'info' | 'warning' | 'error';

export interface SubscriptionView {
  /** False until the till has heard from the server once (never blocks on its own). */
  known: boolean;
  expired: boolean;
  isTrial: boolean;
  packageName: string;
  /** Whole days left (0 = it ends today); null when unknown, expired or open-ended. */
  daysLeft: number | null;
  /** "yyyy-MM-dd"; null when unknown or open-ended (there is no end to show). */
  endDate: string | null;
  /** Checkout, Complete Sale and Exchange are refused. */
  billingBlocked: boolean;
  /** The message for the top of the screen: a trial, ≤ 15 days left, or expired (none for pay as you go). */
  notice: { tone: SubscriptionNoticeTone; message: string } | null;
  /** 'PerBill' = pay as you go (charged per sale bill). */
  billingType: SubscriptionBillingType;
  /** No end date (pay as you go): never expires here, no countdown, no expiry notice. */
  openEnded: boolean;
  /** Pay as you go: the charge per bill, when the server sent it. */
  pricePerBill: number | null;
  /** Display symbol of the plan's currency ("Rs", "$"); null when the server sent none. */
  currencySymbol: string | null;
}

export const UNKNOWN_SUBSCRIPTION_VIEW: SubscriptionView = {
  known: false,
  expired: false,
  isTrial: false,
  packageName: '',
  daysLeft: null,
  endDate: null,
  billingBlocked: false,
  notice: null,
  billingType: 'Subscription',
  openEnded: false,
  pricePerBill: null,
  currencySymbol: null,
};

function pick(source: unknown, ...keys: string[]): unknown {
  if (!source || typeof source !== 'object') return undefined;
  const o = source as Record<string, unknown>;
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
}

function finiteNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function trimmedText(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * An ISO time WITH a zone ("…Z" / "…+05:00") in ms. A zone-less string is refused (null): it is
 * never guessed as UTC or local time.
 */
export function parseZonedIsoMs(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s) || !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(s)) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/** The server's BillingTypes.Normalize: "PerBill" / "Pay as you go" (any case, spaces, - or _) → 'PerBill'. */
export function normalizeBillingType(raw: unknown): SubscriptionBillingType {
  const v = typeof raw === 'string' ? raw.replace(/[\s_-]/g, '').toLowerCase() : '';
  return v === 'perbill' || v === 'payasyougo' ? 'PerBill' : 'Subscription';
}

/** True for an end date ("yyyy-MM-dd") in 2099 or later — the open-ended marker. */
function isOpenEndedYmd(ymd: string | null | undefined): boolean {
  if (!ymd) return false;
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return !!(y && m && d) && Date.UTC(y, m - 1, d) >= OPEN_ENDED_FROM_MS;
}

/** True when the plan has no end: the server said so, or its end is in 2099 or later (older servers). */
export function isOpenEndedSnapshot(s: SubscriptionSnapshot): boolean {
  if (s.isOpenEnded === true) return true;
  if (typeof s.endUtcMs === 'number' && s.endUtcMs >= OPEN_ENDED_FROM_MS) return true;
  return isOpenEndedYmd(s.endDate);
}

/** Display symbols by ISO code — in step with the API's BillingCurrencies (the code is the truth). */
const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  USD: '$', PKR: 'Rs', EUR: '€', GBP: '£', AED: 'د.إ', SAR: '﷼', INR: '₹', CAD: 'C$', AUD: 'A$', TRY: '₺',
  CNY: '¥', JPY: '¥', CHF: 'CHF', KWD: 'KD', QAR: 'QR', BDT: '৳', LKR: 'Rs', OMR: 'OMR', BHD: 'BD', MYR: 'RM',
};

/** The display symbol: the code's own symbol first, then the symbol sent, then the code itself. */
function currencyDisplay(code: string | null | undefined, symbol: string | null | undefined): string | null {
  const c = (code ?? '').trim().toUpperCase();
  if (c && CURRENCY_SYMBOLS[c]) return CURRENCY_SYMBOLS[c];
  return (symbol ?? '').trim() || c || null;
}

/** "Rs 2.00", "$0.03", "$0.025" — a rate keeps up to 4 decimals (as the web's formatMoney does). */
function formatRate(amount: number, symbol: string | null): string {
  const decimals = Math.min(4, Math.max(2, (String(Math.abs(amount)).split('.')[1] ?? '').length));
  const body = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const sign = amount < 0 ? '-' : '';
  if (!symbol) return `${sign}${body}`;
  // Symbols of letters ("Rs", "CHF") read better with a space; "$", "€", "£" without.
  const spaced = /[A-Za-z]$/.test(symbol) || symbol.length > 1;
  return `${sign}${symbol}${spaced ? ' ' : ''}${body}`;
}

/**
 * Read a current-plan answer (camelCase or PascalCase). `deviceNowMs` is this PC's clock when the
 * answer arrived. Null when the answer carries no plan.
 */
export function snapshotFromCurrentPlan(
  response: unknown,
  companyId: string,
  deviceNowMs: number
): SubscriptionSnapshot | null {
  if (!response || typeof response !== 'object') return null;
  const details = pick(response, 'planDetails', 'PlanDetails');
  const packageName = String(pick(response, 'packageName', 'PackageName') ?? '').trim();
  const daysRemaining = finiteNumber(pick(response, 'daysRemaining', 'DaysRemaining'));
  const endRaw = pick(details, 'endDate', 'EndDate');
  const endDate = typeof endRaw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(endRaw) ? endRaw.slice(0, 10) : null;
  const isExpired = pick(response, 'isExpired', 'IsExpired') === true;
  const endUtcMs = parseZonedIsoMs(pick(response, 'endDateUtc', 'EndDateUtc'));
  // Pay as you go: the server's flag (newer servers), else the 2099 end itself (older ones).
  const isOpenEnded =
    pick(response, 'isOpenEnded', 'IsOpenEnded') === true ||
    pick(details, 'isOpenEnded', 'IsOpenEnded') === true ||
    (endUtcMs !== null && endUtcMs >= OPEN_ENDED_FROM_MS) ||
    isOpenEndedYmd(endDate);
  // Nothing to judge by: not a plan answer.
  if (!packageName && daysRemaining === null && endDate === null) return null;
  if (!isExpired && !isOpenEnded && daysRemaining === null && endDate === null) return null;

  const serverNowMs = parseZonedIsoMs(pick(response, 'serverTimeUtc', 'ServerTimeUtc'));
  const pricePerBill = finiteNumber(
    pick(response, 'pricePerBill', 'PricePerBill') ?? pick(details, 'pricePerBill', 'PricePerBill')
  );
  const currencyCode = trimmedText(
    pick(response, 'currencyCode', 'CurrencyCode') ?? pick(details, 'currencyCode', 'CurrencyCode')
  );
  return {
    companyId,
    packageName: packageName || 'Subscription',
    isTrial: pick(details, 'isTrial', 'IsTrial') === true,
    isExpired,
    daysRemaining: Math.max(0, Math.floor(daysRemaining ?? 0)),
    endDate,
    endUtcMs,
    fetchedAtMs: serverNowMs ?? deviceNowMs,
    serverOffsetMs: serverNowMs !== null ? serverNowMs - deviceNowMs : 0,
    serverClock: serverNowMs !== null,
    billingType: normalizeBillingType(
      pick(response, 'billingType', 'BillingType') ?? pick(details, 'billingType', 'BillingType')
    ),
    isOpenEnded,
    pricePerBill: pricePerBill !== null && pricePerBill >= 0 ? pricePerBill : null,
    currencyCode: currencyCode ? currencyCode.toUpperCase() : null,
    currencySymbol: trimmedText(
      pick(response, 'currencySymbol', 'CurrencySymbol') ?? pick(details, 'currencySymbol', 'CurrencySymbol')
    ),
  };
}

/**
 * The moment billing stops (ms, server clock); null for an open-ended (pay-as-you-go) plan, which
 * has no end. The exact end when the server sent it; otherwise the LATEST moment the end date and
 * the day count allow — a till is never blocked before the real end.
 */
export function subscriptionEndMs(s: SubscriptionSnapshot): number | null {
  if (isOpenEndedSnapshot(s)) return null;
  if (s.endUtcMs !== null) return s.endUtcMs;
  const bounds: number[] = [];
  if (s.endDate) {
    const [y, m, d] = s.endDate.split('-').map(Number);
    if (y && m && d) bounds.push(Date.UTC(y, m - 1, d + 1)); // the end of that UTC day
  }
  // (EndDate − answeredAt).Days === daysRemaining  ⇒  EndDate < answeredAt + (daysRemaining + 1) days
  if (!s.isExpired) bounds.push(s.fetchedAtMs + (s.daysRemaining + 1) * DAY_MS);
  return bounds.length > 0 ? Math.min(...bounds) : null;
}

function daysWord(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

/** What the till shows and allows at `nowMs` (a trusted clock — see subscriptionService). */
export function evaluateSubscription(s: SubscriptionSnapshot | null, nowMs: number): SubscriptionView {
  if (!s) return UNKNOWN_SUBSCRIPTION_VIEW;
  const openEnded = isOpenEndedSnapshot(s);
  const end = subscriptionEndMs(s);
  const expired = s.isExpired || (end !== null && nowMs >= end);
  const base = {
    known: true,
    isTrial: s.isTrial,
    packageName: s.packageName,
    endDate: s.endDate,
    billingType: normalizeBillingType(s.billingType),
    pricePerBill: typeof s.pricePerBill === 'number' && Number.isFinite(s.pricePerBill) ? s.pricePerBill : null,
    currencySymbol: currencyDisplay(s.currencyCode, s.currencySymbol),
  };

  if (expired) {
    return {
      ...base,
      expired: true,
      openEnded: false,
      daysLeft: null,
      billingBlocked: true,
      notice: { tone: 'error', message: s.isTrial ? TRIAL_EXPIRED_MESSAGE : SUBSCRIPTION_EXPIRED_MESSAGE },
    };
  }

  // Pay as you go: no end, so no countdown and no expiry notice. A trial keeps its notice (no count).
  if (openEnded) {
    return {
      ...base,
      endDate: null,
      expired: false,
      openEnded: true,
      daysLeft: null,
      billingBlocked: false,
      notice: s.isTrial
        ? {
          tone: 'info',
          message: 'You are currently using the free trial period. Upgrade your plan to continue enjoying all features.',
        }
        : null,
    };
  }

  const daysLeft = s.endUtcMs !== null
    ? Math.max(0, Math.floor((s.endUtcMs - nowMs) / DAY_MS))
    : Math.max(0, s.daysRemaining - Math.max(0, Math.floor((nowMs - s.fetchedAtMs) / DAY_MS)));

  let notice: SubscriptionView['notice'] = null;
  if (s.isTrial) {
    const left = daysLeft === 0 ? 'it ends today' : `${daysWord(daysLeft)} left`;
    notice = {
      tone: daysLeft <= 3 ? 'warning' : 'info',
      message: `You are currently using the free trial period (${left}). Upgrade your plan to continue enjoying all features.`,
    };
  } else if (daysLeft <= SUBSCRIPTION_WARN_DAYS) {
    const when = daysLeft === 0 ? 'today' : `in ${daysWord(daysLeft)}`;
    notice = {
      tone: 'warning',
      message: `Your subscription expires ${when}. Please clear your subscription fee to continue using the system without interruption.`,
    };
  }

  return { ...base, expired: false, openEnded: false, daysLeft, billingBlocked: false, notice };
}

/** The message shown wherever billing is refused. */
export function billingBlockedMessage(view: Pick<SubscriptionView, 'isTrial'>): string {
  return view.isTrial ? TRIAL_EXPIRED_MESSAGE : SUBSCRIPTION_EXPIRED_MESSAGE;
}

/**
 * The plan line under the package name: "12 days left", "1 day left", "Ends today", "Trial ended",
 * "Subscription ended" — and "Pay as you go" for an open-ended plan (no days to count). '' until known.
 */
export function subscriptionDaysLabel(
  view: Pick<SubscriptionView, 'known' | 'expired' | 'isTrial' | 'openEnded' | 'daysLeft'>
): string {
  if (!view.known) return '';
  if (view.expired) return view.isTrial ? 'Trial ended' : 'Subscription ended';
  if (view.openEnded) return PAY_AS_YOU_GO_LABEL;
  if (view.daysLeft === null) return '';
  return view.daysLeft === 0 ? 'Ends today' : `${daysWord(view.daysLeft)} left`;
}

/** "Rs 2.00 per bill", "$0.03 per bill" — the pay-as-you-go rate when the server sent one; else null. */
export function perBillRateLabel(
  view: Pick<SubscriptionView, 'billingType' | 'pricePerBill' | 'currencySymbol'>
): string | null {
  if (view.billingType !== 'PerBill' || view.pricePerBill === null) return null;
  return `${formatRate(view.pricePerBill, view.currencySymbol)} per bill`;
}
